import { PropsWithChildren } from 'react'
import HTMLMapper from 'App/Epi/HTMLMapper'
import {
  goToEnforcedMissingContactInformation,
  isEnforcedToSetContactInformation,
} from 'utils/urlTransition'
import { selectShouldEnforceRoleForFunctionTypes } from './EnforceContactInformation.selectors'
import IllustrationSmartphoneBubbla from './IllustrationSmartphoneBubbla'
import { Box, Button, styled, Typography } from '@mui/material'
import { useHistory, useLocation } from 'react-router-dom'
import { useCachedContentQuery } from 'state/PublicWebApi'
import { useSelector } from 'react-redux'
import { roles } from 'utils/roles'
import { useSliceStateSelector } from 'slices/hooks'
import { useTranslation } from 'react-i18next'

export interface EnforcedFunctionType {
  functionType: string
  message: string
}

const PremableWrapper = styled(Box)(({ theme }) => ({
  p: {
    ...theme.typography.preamble,
  },
}))

const useMessageEnding = () => {
  const { t } = useTranslation()
  const { roles: userRules } = useSliceStateSelector(
    (state) => state.slices.auth.profile
  )
  if (
    userRules?.includes(roles.MissingEmail) &&
    userRules?.includes(roles.MissingMobile)
  ) {
    return t('translation:enforceContactInformation.endingBoth')
  }
  if (userRules.includes(roles.MissingEmail)) {
    return t('translation:enforceContactInformation.endingEmail')
  }
  if (userRules.includes(roles.MissingMobile)) {
    return t('translation:enforceContactInformation.endingMobile')
  }
  return ''
}

export const functionTypesToEnforceRoleFor: EnforcedFunctionType[] = [
  {
    functionType: 'tjanstepersonansokan',
    message: 'translation:enforceContactInformation.tjanstepersonansokan',
  },
  {
    functionType: 'klientansokan',
    message: 'translation:enforceContactInformation.klientansokan',
  },
]

export const getKontoinstallningarLinkBase = (idp = '') =>
  idp === 'kund' ? '/trr-foretag' : '/mitt-trr'

const selectShouldEnforce = (functionType: string) =>
  selectShouldEnforceRoleForFunctionTypes(
    functionTypesToEnforceRoleFor.map(
      (EnforcedFunctionType) => EnforcedFunctionType.functionType
    ),
    functionType
  )

const useMessageStart = (): string | null => {
  const { t } = useTranslation()
  const { data: contentData } = useCachedContentQuery()
  const functionType = contentData.properties.functionType

  let messageStart: string | null = null
  if (functionType === 'tjanstepersonansokan') {
    messageStart = t(
      'translation:enforceContactInformation.tjanstepersonansokan'
    ) as string
  } else if (functionType === 'klientansokan') {
    messageStart = t(
      'translation:enforceContactInformation.klientansokan'
    ) as string
  }
  return messageStart
}

const EnforceContactInformation = ({
  children,
}: PropsWithChildren<unknown>) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { push } = useHistory<isEnforcedToSetContactInformation>()
  const { state } = useLocation<isEnforcedToSetContactInformation>()
  const { profile } = useSliceStateSelector((state) => state.slices.auth)
  const { data: contentData } = useCachedContentQuery()
  const message = `${useMessageStart()} ${useMessageEnding()}`
  const functionType = contentData.properties.functionType
  const heading = contentData.properties.heading
  const preamble = contentData.properties.preamble
  const shouldEnforce = useSelector(selectShouldEnforce(functionType ?? ''))
  const kontoinstallningarLinkBase = getKontoinstallningarLinkBase(profile.idp)

  if (!shouldEnforce) {
    return <>{children}</>
  }

  return (
    <div>
      <Typography variant="h1" gutterBottom>
        {heading}
      </Typography>
      {preamble && (
      <PremableWrapper mb={5}>
        <HTMLMapper body={preamble} />
      </PremableWrapper>
      )}
      <Box
        component={'article'}
        sx={{
          padding: { xs: 3, lg: 5 },
          backgroundColor: 'surface.orange',
          borderRadius: 1,
        }}
      >
        <Box display={'flex'} data-testid="missing-mobile-highlightbox">
          <Box>
            <Typography variant="h3" component="h2" gutterBottom>
              {message}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('translation:enforceContactInformation.description')}
            </Typography>
            {profile.roles?.includes(roles.MissingEmail) && (
              <Button
                data-testid="enforceEmailButton"
                variant="text"
                onClick={() =>
                  goToEnforcedMissingContactInformation(
                    `${kontoinstallningarLinkBase}/kontoinstallningar/andra-epost`,
                    push,
                    state
                  )
                }
              >
                {t('translation:enforceContactInformation.addEmail')}
              </Button>
            )}
            {profile.roles?.includes(roles.MissingMobile) && (
              <Button
                data-testid="enforceMobileButton"
                variant="text"
                onClick={() =>
                  goToEnforcedMissingContactInformation(
                    `${kontoinstallningarLinkBase}/kontoinstallningar/andra-mobilnummer`,
                    push,
                    state
                  )
                }
              >
                {t('translation:enforceContactInformation.addMobile')}
              </Button>
            )}
          </Box>
          <Box display={{ xs: 'none', md: 'block' }}>
            <IllustrationSmartphoneBubbla />
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default EnforceContactInformation
